import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Navbar from "components/Navbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, ReferenceLine, PieChart, Pie,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// STATIC MOCK DATA
// ─────────────────────────────────────────────────────────────────────────────

const CLASS_COLOR = {
  Equity:       "#2b36d9",
  "Fixed Income":"#f59e0b",
  Commodity:    "#04ff1d",
  Alternative:  "#a084c9",
  Cash:         "#939393",
};

const SECTOR_COLOR = {
  Technology:    "#2b36d9",
  Healthcare:    "#06b6d4",
  Financials:    "#f59e0b",
  "Cons. Disc.": "#fd527b",
  Industrials:   "#a084c9",
  Energy:        "#04ff1d",
  Utilities:     "#939393",
  "Real Estate": "#e8643a",
  Materials:     "#84cc16",
};

const COUNTRY_COLOR = {
  USA:   "#2b36d9", GBR:"#f59e0b", DEU:"#04ff1d",
  JPN:   "#a084c9", CHN:"#fd527b", BRA:"#06b6d4",
  Other: "#939393",
};

// ── Cumulative return series (portfolio vs benchmark, 1Y daily) ───────────────
function makeCumRet() {
  const start = new Date("2025-04-01");
  const rows  = [];
  let p = 0, b = 0;
  for (let i = 0; i < 252; i++) {
    const d = new Date(start); d.setDate(d.getDate() + Math.round(i * 365/252));
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    const trend = 0.00045, vol = 0.0072, bTrend = 0.00038, bVol = 0.0055;
    const shock = i > 60 && i < 90 ? -0.003 : 0;
    p += trend + shock + (Math.random() - 0.49) * vol;
    b += bTrend +        (Math.random() - 0.49) * bVol;
    rows.push({
      date:  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`,
      port:  +( p * 100).toFixed(2),
      bmk:   +( b * 100).toFixed(2),
      active:+((p - b) * 100).toFixed(2),
    });
  }
  return rows;
}
const CUMRET_DATA = makeCumRet();

// ── Period return table ───────────────────────────────────────────────────────
const PERIOD_RETURNS = [
  { class:"Equity",        day:0.42,  mtd:1.83,  ytd:6.21,  one:14.30, bmkYtd:5.10,  bmkOne:12.80 },
  { class:"Fixed Income",  day:0.05,  mtd:0.42,  ytd:1.88,  one:4.20,  bmkYtd:1.50,  bmkOne:3.80  },
  { class:"Commodity",     day:-0.18, mtd:-0.95, ytd:3.10,  one:8.90,  bmkYtd:2.80,  bmkOne:7.50  },
  { class:"Alternative",   day:0.11,  mtd:0.58,  ytd:2.40,  one:5.60,  bmkYtd:2.10,  bmkOne:4.90  },
  { class:"Cash",          day:0.02,  mtd:0.08,  ytd:0.40,  one:1.20,  bmkYtd:0.40,  bmkOne:1.20  },
  { class:"Portfolio",     day:0.28,  mtd:1.12,  ytd:4.68,  one:11.40, bmkYtd:3.90,  bmkOne:10.20 },
];

// ── Top contributors / detractors ─────────────────────────────────────────────
const CONTRIBUTORS = [
  { ticker:"NVDA",  name:"NVIDIA Corp.",           ac:"EQ", contrib: 1.82, ret: 22.1  },
  { ticker:"AAPL",  name:"Apple Inc.",             ac:"EQ", contrib: 0.94, ret:  8.7  },
  { ticker:"GLD",   name:"SPDR Gold Shares",       ac:"CD", contrib: 0.52, ret: 10.8  },
  { ticker:"MSFT",  name:"Microsoft Corp.",        ac:"EQ", contrib: 0.48, ret:  4.4  },
  { ticker:"BRK.B", name:"Berkshire Hathaway B",   ac:"EQ", contrib: 0.31, ret:  7.4  },
  { ticker:"HYG",   name:"iShares High Yield Corp",ac:"FI", contrib:-0.18, ret: -3.5  },
  { ticker:"DBC",   name:"Invesco DB Commodity",   ac:"CD", contrib:-0.25, ret: -7.8  },
  { ticker:"TLT",   name:"iShares 20+ Yr Treasury",ac:"FI", contrib:-0.38, ret: -6.0  },
];

// ── Allocation by dimension ───────────────────────────────────────────────────
const ALLOC_CLASS = [
  { name:"Equity",       weight:50.1, bmk:43.0 },
  { name:"Fixed Income", weight:19.9, bmk:25.0 },
  { name:"Commodity",    weight:8.0,  bmk:5.0  },
  { name:"Alternative",  weight:6.7,  bmk:7.0  },
  { name:"Cash",         weight:15.3, bmk:20.0 },
];

const ALLOC_SECTOR = [
  { name:"Technology",   weight:28.4, bmk:22.0 },
  { name:"Healthcare",   weight:7.2,  bmk:8.5  },
  { name:"Financials",   weight:6.8,  bmk:9.0  },
  { name:"Cons. Disc.",  weight:5.9,  bmk:6.5  },
  { name:"Industrials",  weight:4.4,  bmk:5.5  },
  { name:"Energy",       weight:3.1,  bmk:4.0  },
  { name:"Real Estate",  weight:6.7,  bmk:7.0  },
  { name:"Utilities",    weight:2.2,  bmk:3.0  },
  { name:"Materials",    weight:1.6,  bmk:2.5  },
  { name:"Cash",         weight:15.3, bmk:20.0 },
  { name:"Other",        weight:18.4, bmk:12.0 },
];

const ALLOC_COUNTRY = [
  { name:"USA",   weight:68.2, bmk:63.0 },
  { name:"GBR",   weight:5.4,  bmk:6.0  },
  { name:"DEU",   weight:3.8,  bmk:4.5  },
  { name:"JPN",   weight:4.1,  bmk:5.0  },
  { name:"CHN",   weight:2.9,  bmk:4.0  },
  { name:"BRA",   weight:1.5,  bmk:1.5  },
  { name:"Other", weight:14.1, bmk:16.0 },
];

const ALLOC_CURRENCY = [
  { name:"USD", weight:71.2, bmk:65.0 },
  { name:"EUR", weight:10.8, bmk:12.0 },
  { name:"GBP", weight:5.4,  bmk:6.0  },
  { name:"JPY", weight:4.1,  bmk:5.0  },
  { name:"Other",weight:8.5, bmk:12.0 },
];

// Allocation over time (monthly, last 12 months)
const ALLOC_TREND = [
  { month:"Apr'25", Equity:46.2, "Fixed Income":22.1, Commodity:7.8, Alternative:6.4, Cash:17.5 },
  { month:"May'25", Equity:47.0, "Fixed Income":21.5, Commodity:8.1, Alternative:6.5, Cash:16.9 },
  { month:"Jun'25", Equity:46.5, "Fixed Income":21.0, Commodity:8.4, Alternative:6.6, Cash:17.5 },
  { month:"Jul'25", Equity:47.8, "Fixed Income":20.8, Commodity:8.0, Alternative:6.5, Cash:16.9 },
  { month:"Aug'25", Equity:48.2, "Fixed Income":20.5, Commodity:7.9, Alternative:6.6, Cash:16.8 },
  { month:"Sep'25", Equity:48.8, "Fixed Income":20.2, Commodity:8.1, Alternative:6.6, Cash:16.3 },
  { month:"Oct'25", Equity:49.1, "Fixed Income":20.1, Commodity:7.9, Alternative:6.7, Cash:16.2 },
  { month:"Nov'25", Equity:49.5, "Fixed Income":20.2, Commodity:8.0, Alternative:6.7, Cash:15.6 },
  { month:"Dec'25", Equity:50.0, "Fixed Income":20.0, Commodity:8.1, Alternative:6.7, Cash:15.2 },
  { month:"Jan'26", Equity:49.8, "Fixed Income":20.1, Commodity:8.0, Alternative:6.6, Cash:15.5 },
  { month:"Feb'26", Equity:50.2, "Fixed Income":19.9, Commodity:8.1, Alternative:6.7, Cash:15.1 },
  { month:"Mar'26", Equity:50.1, "Fixed Income":19.9, Commodity:8.0, Alternative:6.7, Cash:15.3 },
];

// ── Benchmark: active weights + tracking ─────────────────────────────────────
const ACTIVE_WEIGHTS = [
  { ticker:"NVDA",  name:"NVIDIA Corp.",            ac:"EQ", portW:8.2,  bmkW:6.0,  active:+2.2, contrib: 1.82 },
  { ticker:"AAPL",  name:"Apple Inc.",              ac:"EQ", portW:12.5, bmkW:10.0, active:+2.5, contrib: 0.94 },
  { ticker:"CASH",  name:"Cash & Equivalents",      ac:"CS", portW:15.3, bmkW:20.0, active:-4.7, contrib:-0.08 },
  { ticker:"TLT",   name:"iShares 20+ Yr Treasury", ac:"FI", portW:6.3,  bmkW:8.0,  active:-1.7, contrib:-0.38 },
  { ticker:"MSFT",  name:"Microsoft Corp.",         ac:"EQ", portW:10.8, bmkW:9.0,  active:+1.8, contrib: 0.48 },
  { ticker:"LQD",   name:"iShares Corp Bond ETF",   ac:"FI", portW:8.5,  bmkW:9.0,  active:-0.5, contrib: 0.12 },
  { ticker:"GLD",   name:"SPDR Gold Shares",        ac:"CD", portW:4.8,  bmkW:5.0,  active:-0.2, contrib: 0.52 },
  { ticker:"AMZN",  name:"Amazon.com Inc.",         ac:"EQ", portW:6.9,  bmkW:6.0,  active:+0.9, contrib: 0.22 },
  { ticker:"GOOGL", name:"Alphabet Inc.",           ac:"EQ", portW:7.5,  bmkW:7.0,  active:+0.5, contrib: 0.18 },
  { ticker:"HYG",   name:"iShares High Yield Corp", ac:"FI", portW:5.1,  bmkW:5.0,  active:+0.1, contrib:-0.18 },
  { ticker:"BRK.B", name:"Berkshire Hathaway B",    ac:"EQ", portW:4.2,  bmkW:5.0,  active:-0.8, contrib: 0.31 },
  { ticker:"DBC",   name:"Invesco DB Commodity",    ac:"CD", portW:3.2,  bmkW:3.0,  active:+0.2, contrib:-0.25 },
  { ticker:"REET",  name:"iShares Global REIT",     ac:"MF", portW:3.8,  bmkW:4.0,  active:-0.2, contrib: 0.08 },
  { ticker:"QAI",   name:"IQ Hedge Multi-Strat",    ac:"MF", portW:2.9,  bmkW:3.0,  active:-0.1, contrib: 0.06 },
];

// Rolling tracking error (monthly)
const TRACKING_ERROR_DATA = [
  { month:"Apr'25", te:1.82 }, { month:"May'25", te:1.95 }, { month:"Jun'25", te:2.10 },
  { month:"Jul'25", te:2.22 }, { month:"Aug'25", te:2.35 }, { month:"Sep'25", te:2.18 },
  { month:"Oct'25", te:2.05 }, { month:"Nov'25", te:1.98 }, { month:"Dec'25", te:1.88 },
  { month:"Jan'26", te:1.92 }, { month:"Feb'26", te:1.97 }, { month:"Mar'26", te:2.04 },
];

// Active return (monthly)
const ACTIVE_RETURN_DATA = [
  { month:"Apr'25", ar: 0.28 }, { month:"May'25", ar: 0.42 }, { month:"Jun'25", ar:-0.15 },
  { month:"Jul'25", ar: 0.38 }, { month:"Aug'25", ar:-0.22 }, { month:"Sep'25", ar: 0.51 },
  { month:"Oct'25", ar: 0.33 }, { month:"Nov'25", ar: 0.18 }, { month:"Dec'25", ar: 0.46 },
  { month:"Jan'26", ar:-0.08 }, { month:"Feb'26", ar: 0.35 }, { month:"Mar'26", ar: 0.52 },
];

// Summary benchmark metrics
const BMK_SUMMARY = {
  activeReturn1Y:    1.20,
  trackingError:     2.04,
  informationRatio:  0.59,
  beta:              0.92,
  alpha:             1.08,
  correlationVsBmk:  0.97,
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtDateShort(iso) {
  const [y, m, d] = iso.split("-");
  return `${MONTHS[+m-1]} ${+d}`;
}

function fmtPct(n, d=2, sign=false) {
  if (n == null) return "—";
  const s = sign && n > 0 ? "+" : "";
  return `${s}${n.toFixed(d)}%`;
}

function fmtNum(n, d=2, sign=false) {
  if (n == null) return "—";
  const s = sign && n > 0 ? "+" : "";
  return `${s}${n.toFixed(d)}`;
}

function periodSubset(data, period) {
  if (period === "1M")  return data.slice(-21);
  if (period === "3M")  return data.slice(-63);
  if (period === "YTD") return data.filter(d => d.date >= `${new Date().getFullYear()}-01-01`);
  if (period === "1Y")  return data;
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SMALL COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, accentColor, i = 0 }) {
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10,
      padding:"16px", position:"relative", animation:`fadeSlideUp 0.4s ${0.04+i*0.05}s ease both` }}>
      {accentColor && <div style={{ position:"absolute", top:0, left:0, right:0, height:2, borderRadius:"10px 10px 0 0", background:accentColor }} />}
      <div style={{ fontSize:9, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, fontFamily:"monospace", color:"var(--white_a700)" }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:"var(--gray_500)", marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div style={{ fontSize:10, letterSpacing:"3px", textTransform:"uppercase", color:"var(--indigo_a700)", marginBottom:14 }}>{children}</div>;
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"20px 24px", ...style }}>
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return <div style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)", marginBottom:14 }}>{children}</div>;
}

function ChartTooltip({ active, payload, label, unit = "%" }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"10px 14px", minWidth:160 }}>
      <div style={{ fontSize:11, color:"var(--gray_500)", marginBottom:6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:16, marginBottom:3 }}>
          <span style={{ fontSize:11, color:p.stroke || p.fill }}>{p.name}</span>
          <span style={{ fontSize:12, fontFamily:"monospace", color:"var(--white_a700)", fontWeight:600 }}>
            {p.value >= 0 ? "+" : ""}{p.value?.toFixed(2)}{unit}
          </span>
        </div>
      ))}
    </div>
  );
}

function ClassDot({ ac }) {
  const color = CLASS_COLOR[ac] ?? "#888";
  return <div style={{ width:6, height:6, borderRadius:2, background:color, flexShrink:0 }} />;
}

function PeriodSelector({ value, onChange }) {
  return (
    <div style={{ display:"flex", gap:4 }}>
      {["1M","3M","YTD","1Y"].map(p => (
        <button key={p} onClick={() => onChange(p)} style={{
          padding:"4px 10px", fontSize:11, fontWeight:600, borderRadius:5, cursor:"pointer",
          border:"1px solid var(--gray_900)",
          background: value===p ? "var(--indigo_a700)" : "var(--gray_900_02)",
          color:       value===p ? "#fff"               : "var(--gray_500)",
          transition:"all 0.15s",
        }}>{p}</button>
      ))}
    </div>
  );
}

function AllocTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"10px 14px", minWidth:180 }}>
      <div style={{ fontSize:11, color:"var(--gray_500)", marginBottom:6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:16, marginBottom:3 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <div style={{ width:8, height:8, borderRadius:2, background:p.fill }} />
            <span style={{ fontSize:11, color:"var(--gray_400)" }}>{p.name}</span>
          </div>
          <span style={{ fontSize:12, fontFamily:"monospace", color:"var(--white_a700)" }}>{p.value?.toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: PERFORMANCE
// ─────────────────────────────────────────────────────────────────────────────

function PerformanceTab() {
  const [period, setPeriod] = useState("1Y");
  const chartData = useMemo(() => {
    const sub = periodSubset(CUMRET_DATA, period);
    // Show every Nth label to avoid crowding
    const step = period === "1M" ? 3 : period === "3M" ? 7 : period === "YTD" ? 10 : 15;
    return sub.map((d, i) => ({
      ...d,
      label: i % step === 0 ? fmtDateShort(d.date) : "",
    }));
  }, [period]);

  const lastPort   = chartData.at(-1)?.port   ?? 0;
  const lastBmk    = chartData.at(-1)?.bmk    ?? 0;
  const lastActive = chartData.at(-1)?.active ?? 0;

  const portColor   = lastPort   >= 0 ? "#04ff1d" : "#ff2e2e";
  const bmkColor    = lastBmk    >= 0 ? "#f59e0b" : "#ff2e2e";
  const activeColor = lastActive >= 0 ? "#06b6d4" : "#ff2e2e";

  const th = { padding:"10px 14px", fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", textAlign:"right" };
  const td = (color="var(--gray_400)") => ({ padding:"10px 14px", fontSize:13, fontFamily:"monospace", textAlign:"right", color });
  const retColor = (v) => v > 0 ? "#04ff1d" : v < 0 ? "#ff2e2e" : "var(--gray_500)";

  return (
    <>
      {/* Summary cards */}
      <SectionLabel>Performance Summary — 1 Year</SectionLabel>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:28 }}>
        <MetricCard label="Portfolio Return (1Y)"  value={fmtPct(lastPort,   2, true)} accentColor={portColor}   i={0} sub="vs prior year-end" />
        <MetricCard label="Benchmark Return (1Y)"  value={fmtPct(lastBmk,    2, true)} accentColor={bmkColor}    i={1} sub="blended benchmark" />
        <MetricCard label="Active Return (1Y)"     value={fmtPct(lastActive, 2, true)} accentColor={activeColor} i={2} sub="portfolio vs bmk" />
        <MetricCard label="Sharpe Ratio"           value="1.34"                        accentColor="#a084c9"     i={3} sub="annualized" />
      </div>

      {/* Cumulative return chart */}
      <SectionLabel>Cumulative Return</SectionLabel>
      <Card style={{ marginBottom:28 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <CardTitle>Portfolio vs Benchmark</CardTitle>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ left:0, right:20, top:5, bottom:0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="label" tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} interval={0} />
            <YAxis tickFormatter={v => `${v>0?"+":""}${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} width={52} />
            <Tooltip content={<ChartTooltip unit="%" />} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.12)" />
            <Legend formatter={v => <span style={{ fontSize:11, color:"var(--gray_400)" }}>{v}</span>} wrapperStyle={{ paddingTop:8 }} />
            <Line type="monotone" dataKey="port"   name="Portfolio" stroke="#2b36d9" strokeWidth={2} dot={false} activeDot={{ r:4 }} />
            <Line type="monotone" dataKey="bmk"    name="Benchmark" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 4" dot={false} activeDot={{ r:3 }} />
            <Line type="monotone" dataKey="active" name="Active"    stroke="#06b6d4" strokeWidth={1.5} strokeDasharray="3 3" dot={false} activeDot={{ r:3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
        {/* Period return table */}
        <Card>
          <CardTitle>Period Returns by Asset Class</CardTitle>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                  <th style={{ ...th, textAlign:"left", width:"28%" }}>Class</th>
                  <th style={th}>Day</th>
                  <th style={th}>MTD</th>
                  <th style={th}>YTD</th>
                  <th style={th}>1Y</th>
                  <th style={{ ...th, color:"var(--gray_500)", opacity:0.6 }}>Bmk YTD</th>
                </tr>
              </thead>
              <tbody>
                {PERIOD_RETURNS.map((r, i) => {
                  const isTotal = r.class === "Portfolio";
                  return (
                    <tr key={r.class} style={{
                      borderBottom:"1px solid rgba(255,255,255,0.04)",
                      background: isTotal ? "rgba(43,54,217,0.07)" : i%2===0 ? "transparent" : "rgba(255,255,255,0.01)",
                    }}>
                      <td style={{ padding:"10px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          {!isTotal && <div style={{ width:6, height:6, borderRadius:2, background:CLASS_COLOR[r.class]??"#888", flexShrink:0 }} />}
                          <span style={{ fontSize:12, fontWeight:isTotal?700:400, color:isTotal?"var(--white_a700)":"var(--gray_400)" }}>{r.class}</span>
                        </div>
                      </td>
                      <td style={td(retColor(r.day))}>{fmtPct(r.day,2,true)}</td>
                      <td style={td(retColor(r.mtd))}>{fmtPct(r.mtd,2,true)}</td>
                      <td style={td(retColor(r.ytd))}>{fmtPct(r.ytd,2,true)}</td>
                      <td style={td(retColor(r.one))}>{fmtPct(r.one,2,true)}</td>
                      <td style={{ ...td("var(--gray_500)"), fontSize:12, opacity:0.75 }}>{fmtPct(r.bmkYtd,2,true)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top contributors / detractors */}
        <Card>
          <CardTitle>Top Contributors & Detractors — YTD</CardTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={[...CONTRIBUTORS].sort((a,b)=>b.contrib-a.contrib)}
              layout="vertical"
              margin={{ left:8, right:40, top:0, bottom:0 }}
            >
              <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.04)" />
              <XAxis type="number" tickFormatter={v=>`${v>0?"+":""}${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="ticker" tick={{ fontSize:11, fill:"var(--gray_400)", fontFamily:"monospace" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = CONTRIBUTORS.find(c=>c.ticker===label);
                return (
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"10px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--white_a700)", marginBottom:4 }}>{row?.name}</div>
                    <div style={{ fontSize:11, color:"var(--gray_500)" }}>Contribution: <span style={{ color:payload[0].value>=0?"#04ff1d":"#ff2e2e", fontFamily:"monospace" }}>{payload[0].value>=0?"+":""}{payload[0].value?.toFixed(2)}%</span></div>
                    <div style={{ fontSize:11, color:"var(--gray_500)", marginTop:2 }}>Return: <span style={{ color:"var(--gray_400)", fontFamily:"monospace" }}>{row?.ret>=0?"+":""}{row?.ret?.toFixed(1)}%</span></div>
                  </div>
                );
              }} />
              <Bar dataKey="contrib" name="Contribution" radius={[0,3,3,0]} maxBarSize={14}>
                {CONTRIBUTORS.map((d, i) => <Cell key={i} fill={d.contrib >= 0 ? "#04ff1d" : "#ff2e2e"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: ALLOCATION
// ─────────────────────────────────────────────────────────────────────────────

function AllocationTab() {
  const [dim, setDim] = useState("class");

  const DIM_OPTIONS = [
    { id:"class",    label:"Asset Class",  data:ALLOC_CLASS,    colorMap:CLASS_COLOR    },
    { id:"sector",   label:"Sector",       data:ALLOC_SECTOR,   colorMap:SECTOR_COLOR   },
    { id:"country",  label:"Country",      data:ALLOC_COUNTRY,  colorMap:COUNTRY_COLOR  },
    { id:"currency", label:"Currency",     data:ALLOC_CURRENCY, colorMap:{}             },
  ];

  const current = DIM_OPTIONS.find(d => d.id === dim);
  const dimData = current.data;
  const cMap    = current.colorMap;

  // Build delta data
  const deltaData = dimData.map(d => ({
    name:  d.name,
    delta: +(d.weight - d.bmk).toFixed(2),
  })).filter(d => Math.abs(d.delta) >= 0.1).sort((a,b) => Math.abs(b.delta)-Math.abs(a.delta));

  const CURRENCY_COLORS = ["#2b36d9","#f59e0b","#06b6d4","#a084c9","#939393"];

  const th = { padding:"10px 14px", fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px" };

  return (
    <>
      {/* Dimension selector */}
      <div style={{ display:"flex", gap:6, marginBottom:24 }}>
        {DIM_OPTIONS.map(o => (
          <button key={o.id} onClick={() => setDim(o.id)} style={{
            padding:"7px 16px", fontSize:12, fontWeight:600, borderRadius:6, cursor:"pointer",
            border:"1px solid var(--gray_900)",
            background: dim===o.id ? "var(--indigo_a700)" : "var(--gray_900_01)",
            color:       dim===o.id ? "#fff"               : "var(--gray_500)",
            transition:"all 0.15s",
          }}>{o.label}</button>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>

        {/* Portfolio vs Benchmark donut pair */}
        <Card>
          <CardTitle>Current Allocation — {current.label}</CardTitle>
          <div style={{ display:"flex", gap:20, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, textAlign:"center" }}>Portfolio</div>
              <ResponsiveContainer width={110} height={110}>
                <PieChart>
                  <Pie data={dimData} dataKey="weight" cx="50%" cy="50%" innerRadius={28} outerRadius={52} paddingAngle={2} startAngle={90} endAngle={-270}>
                    {dimData.map((d,i) => <Cell key={i} fill={cMap[d.name] ?? CURRENCY_COLORS[i % CURRENCY_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1px", marginBottom:6, textAlign:"center" }}>Benchmark</div>
              <ResponsiveContainer width={110} height={110}>
                <PieChart>
                  <Pie data={dimData} dataKey="bmk" cx="50%" cy="50%" innerRadius={28} outerRadius={52} paddingAngle={2} startAngle={90} endAngle={-270}>
                    {dimData.map((d,i) => <Cell key={i} fill={cMap[d.name] ?? CURRENCY_COLORS[i % CURRENCY_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex:1, display:"flex", flexDirection:"column", gap:5 }}>
              {dimData.map((d,i) => {
                const color = cMap[d.name] ?? CURRENCY_COLORS[i % CURRENCY_COLORS.length];
                const delta = +(d.weight - d.bmk).toFixed(1);
                return (
                  <div key={d.name} style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"space-between" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5, minWidth:0 }}>
                      <div style={{ width:7, height:7, borderRadius:2, background:color, flexShrink:0 }} />
                      <span style={{ fontSize:11, color:"var(--gray_400)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{d.name}</span>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                      <span style={{ fontSize:12, fontFamily:"monospace", color:"var(--white_a700)" }}>{d.weight.toFixed(1)}%</span>
                      <span style={{ fontSize:10, fontFamily:"monospace", color:delta>0.5?"#f59e0b":delta<-0.5?"#04ff1d":"var(--gray_500)" }}>
                        {delta >= 0 ? "+" : ""}{delta.toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Active weight delta bar */}
        <Card>
          <CardTitle>Active Weights vs Benchmark — {current.label}</CardTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={deltaData} layout="vertical" margin={{ left:8, right:30, top:0, bottom:0 }}>
              <CartesianGrid horizontal={false} stroke="rgba(255,255,255,0.04)" />
              <XAxis type="number" tickFormatter={v=>`${v>0?"+":""}${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize:11, fill:"var(--gray_400)" }} axisLine={false} tickLine={false} width={80} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const v = payload[0]?.value;
                return (
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"8px 12px" }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"var(--white_a700)", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:12, fontFamily:"monospace", color:v>=0?"#f59e0b":"#04ff1d" }}>
                      {v>=0?"+":""}{v?.toFixed(2)}% vs benchmark
                    </div>
                  </div>
                );
              }} />
              <ReferenceLine x={0} stroke="rgba(255,255,255,0.18)" />
              <Bar dataKey="delta" name="Active Weight" radius={[0,4,4,0]} maxBarSize={16}>
                {deltaData.map((d,i) => <Cell key={i} fill={d.delta >= 0 ? "#f59e0b" : "#04ff1d"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Allocation over time — stacked area */}
      <SectionLabel>Allocation Trend — Last 12 Months</SectionLabel>
      <Card style={{ marginBottom:28 }}>
        <CardTitle>Allocation by Asset Class Over Time</CardTitle>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={ALLOC_TREND} margin={{ left:0, right:20, top:5, bottom:0 }} stackOffset="expand">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v=>`${(v*100).toFixed(0)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} width={40} />
            <Tooltip content={<AllocTooltip />} />
            <Legend formatter={v => <span style={{ fontSize:11, color:"var(--gray_400)" }}>{v}</span>} wrapperStyle={{ paddingTop:8 }} />
            {Object.entries(CLASS_COLOR).map(([k,c]) => (
              <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={c} fill={c} fillOpacity={0.7} strokeWidth={1} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Detailed allocation table */}
      <SectionLabel>Detailed Allocation — {current.label}</SectionLabel>
      <Card>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
              <th style={{ ...th, textAlign:"left" }}>{current.label}</th>
              <th style={{ ...th, textAlign:"right" }}>Portfolio %</th>
              <th style={{ ...th, textAlign:"right" }}>Portfolio $</th>
              <th style={{ ...th, textAlign:"right" }}>Benchmark %</th>
              <th style={{ ...th, textAlign:"right" }}>Active Weight</th>
            </tr>
          </thead>
          <tbody>
            {dimData.map((d, i) => {
              const color  = cMap[d.name] ?? CURRENCY_COLORS[i % CURRENCY_COLORS.length];
              const delta  = +(d.weight - d.bmk).toFixed(2);
              const portMV = (d.weight * 24_700_000 / 100);
              return (
                <tr key={d.name} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:6, height:6, borderRadius:2, background:color, flexShrink:0 }} />
                      <span style={{ fontSize:12, color:"var(--gray_400)" }}>{d.name}</span>
                    </div>
                  </td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", color:"var(--white_a700)", fontWeight:600 }}>{d.weight.toFixed(1)}%</td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, fontFamily:"monospace", color:"var(--gray_400)" }}>
                    ${portMV >= 1e6 ? (portMV/1e6).toFixed(2)+"M" : (portMV/1e3).toFixed(0)+"K"}
                  </td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontSize:12, fontFamily:"monospace", color:"var(--gray_500)" }}>{d.bmk.toFixed(1)}%</td>
                  <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", fontWeight:600,
                    color: Math.abs(delta)<0.3 ? "var(--gray_500)" : delta>0 ? "#f59e0b" : "#04ff1d" }}>
                    {delta >= 0 ? "+" : ""}{delta.toFixed(2)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: BENCHMARK
// ─────────────────────────────────────────────────────────────────────────────

function BenchmarkTab() {
  const [sortKey, setSortKey] = useState("active");
  const [sortDir, setSortDir] = useState("desc");

  function handleSort(k) {
    if (sortKey === k) setSortDir(d => d==="asc"?"desc":"asc");
    else { setSortKey(k); setSortDir("desc"); }
  }

  const sorted = useMemo(() => {
    return [...ACTIVE_WEIGHTS].sort((a,b) => {
      const av = a[sortKey], bv = b[sortKey];
      return sortDir==="asc" ? av-bv : bv-av;
    });
  }, [sortKey, sortDir]);

  const sortIcon = (k) => sortKey!==k
    ? <span style={{ opacity:0.2, marginLeft:3 }}>↕</span>
    : <span style={{ marginLeft:3, color:"var(--indigo_a700)" }}>{sortDir==="asc"?"↑":"↓"}</span>;

  const th = (right=false, k) => ({
    padding:"10px 14px", textAlign:right?"right":"left",
    fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px",
    cursor:"pointer", userSelect:"none", whiteSpace:"nowrap",
  });

  const infoRatioColor = BMK_SUMMARY.informationRatio >= 0.5 ? "#04ff1d"
    : BMK_SUMMARY.informationRatio >= 0 ? "#f59e0b" : "#ff2e2e";

  return (
    <>
      {/* Summary metric cards */}
      <SectionLabel>Benchmark Summary — 1 Year</SectionLabel>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:28 }}>
        <MetricCard label="Active Return (1Y)"    value={fmtPct(BMK_SUMMARY.activeReturn1Y,   2, true)} accentColor="#04ff1d"  i={0} sub="portfolio minus benchmark" />
        <MetricCard label="Tracking Error (Ann.)" value={fmtPct(BMK_SUMMARY.trackingError,    2)}       accentColor="#f59e0b"  i={1} sub="annualized active vol" />
        <MetricCard label="Information Ratio"     value={fmtNum(BMK_SUMMARY.informationRatio, 2)}       accentColor={infoRatioColor} i={2} sub="active return / tracking error" />
        <MetricCard label="Portfolio Beta"        value={fmtNum(BMK_SUMMARY.beta, 2)}                  accentColor="#a084c9"  i={3} sub="vs benchmark" />
        <MetricCard label="Alpha (Ann.)"          value={fmtPct(BMK_SUMMARY.alpha, 2, true)}           accentColor="#2b36d9"  i={4} sub="Jensen's alpha" />
        <MetricCard label="Correlation vs Bmk"   value={fmtNum(BMK_SUMMARY.correlationVsBmk,2)}        accentColor="#06b6d4"  i={5} sub="R² = 0.94" />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
        {/* Tracking error over time */}
        <Card>
          <CardTitle>Rolling Tracking Error (Monthly)</CardTitle>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={TRACKING_ERROR_DATA} margin={{ left:0, right:10, top:5, bottom:0 }}>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize:9, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v=>`${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} width={36} domain={[0,"auto"]} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"8px 12px" }}>
                    <div style={{ fontSize:10, color:"var(--gray_500)", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:13, fontFamily:"monospace", color:"#f59e0b", fontWeight:600 }}>{payload[0]?.value?.toFixed(2)}%</div>
                    <div style={{ fontSize:10, color:"var(--gray_500)" }}>Tracking Error</div>
                  </div>
                );
              }} />
              <Bar dataKey="te" name="Tracking Error" fill="#f59e0b" fillOpacity={0.8} radius={[3,3,0,0]} maxBarSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Monthly active return */}
        <Card>
          <CardTitle>Monthly Active Return</CardTitle>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={ACTIVE_RETURN_DATA} margin={{ left:0, right:10, top:5, bottom:0 }}>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize:9, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v=>`${v>0?"+":""}${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const v = payload[0]?.value;
                return (
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"8px 12px" }}>
                    <div style={{ fontSize:10, color:"var(--gray_500)", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:13, fontFamily:"monospace", color:v>=0?"#04ff1d":"#ff2e2e", fontWeight:600 }}>{v>=0?"+":""}{v?.toFixed(2)}%</div>
                    <div style={{ fontSize:10, color:"var(--gray_500)" }}>Active Return</div>
                  </div>
                );
              }} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
              <Bar dataKey="ar" name="Active Return" radius={[3,3,0,0]} maxBarSize={24}>
                {ACTIVE_RETURN_DATA.map((d,i) => <Cell key={i} fill={d.ar >= 0 ? "#04ff1d" : "#ff2e2e"} fillOpacity={0.85} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Active weights table */}
      <SectionLabel>Active Weights by Position</SectionLabel>
      <Card>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                <th style={{ ...th(), cursor:"default" }}>Ticker</th>
                <th style={{ ...th(), cursor:"default" }}>Name</th>
                <th style={{ ...th(), cursor:"default" }}>Class</th>
                <th style={{ ...th(true), cursor:"pointer" }} onClick={() => handleSort("portW")}>Port %{sortIcon("portW")}</th>
                <th style={{ ...th(true), cursor:"pointer" }} onClick={() => handleSort("bmkW")}>Bmk %{sortIcon("bmkW")}</th>
                <th style={{ ...th(true), cursor:"pointer" }} onClick={() => handleSort("active")}>Active Wt{sortIcon("active")}</th>
                <th style={{ ...th(true), cursor:"pointer" }} onClick={() => handleSort("contrib")}>YTD Contrib{sortIcon("contrib")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const aClr = Math.abs(r.active) < 0.3 ? "var(--gray_500)" : r.active > 0 ? "#f59e0b" : "#04ff1d";
                const cClr = Math.abs(r.contrib) < 0.05 ? "var(--gray_500)" : r.contrib > 0 ? "#04ff1d" : "#ff2e2e";
                return (
                  <tr key={r.ticker} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                    <td style={{ padding:"10px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <ClassDot ac={CLASS_COLOR[r.ac] ? r.ac : "EQ"} />
                        <span style={{ fontSize:13, fontFamily:"monospace", fontWeight:700, color:"var(--white_a700)" }}>{r.ticker}</span>
                      </div>
                    </td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:"var(--gray_400)", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ fontSize:10, padding:"2px 7px", borderRadius:4, fontWeight:600, background:`${CLASS_COLOR[r.ac]??"#888"}22`, color:CLASS_COLOR[r.ac]??"#888" }}>
                        {r.ac}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", color:"var(--white_a700)" }}>{r.portW.toFixed(1)}%</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", color:"var(--gray_500)" }}>{r.bmkW.toFixed(1)}%</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", fontWeight:600, color:aClr }}>
                      {r.active >= 0 ? "+" : ""}{r.active.toFixed(1)}%
                    </td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", fontWeight:600, color:cClr }}>
                      {r.contrib >= 0 ? "+" : ""}{r.contrib.toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id:"performance", label:"Performance"       },
  { id:"allocation",  label:"Allocation"        },
  { id:"benchmark",   label:"Benchmark Tracking"},
];

export default function Analytics() {
  const [activeTab, setActiveTab] = useState("performance");

  const tabBtn = (id) => {
    const active = activeTab === id;
    return {
      padding:"10px 20px", fontSize:13, fontWeight:active?600:400,
      color:active?"var(--white_a700)":"var(--gray_500)",
      border:"none", borderBottom:active?"2px solid var(--indigo_a700)":"2px solid transparent",
      marginBottom:-1, background:"transparent", cursor:"pointer", outline:"none",
      letterSpacing:"0.3px", transition:"color 0.15s",
    };
  };

  return (
    <>
      <Helmet>
        <title>Analytics – Tail Risk Global</title>
        <meta name="description" content="Portfolio performance, allocation, and benchmark analytics." />
      </Helmet>

      <div className="flex flex-col min-h-screen bg-gray-900_02">
        <div className="w-full"><Navbar /></div>

        <div className="flex flex-1" style={{ borderTop:"1px solid var(--gray_900)" }}>
          <DashboardMenuBar activePage="Analytics" />

          <main style={{ flex:1, padding:"36px 40px", overflowX:"hidden" }}>

            {/* Tab bar */}
            <div style={{ display:"flex", borderBottom:"1px solid var(--gray_900)", marginBottom:28 }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabBtn(t.id)}>{t.label}</button>
              ))}
            </div>

            {/* Page header */}
            <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:28 }}>
              <div>
                <div style={{ fontSize:11, color:"var(--gray_500)", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:4 }}>Portfolio</div>
                <div style={{ fontSize:26, fontWeight:600, color:"var(--white_a700)", letterSpacing:"0.3px" }}>Analytics</div>
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <div style={{ padding:"5px 10px", borderRadius:4, fontSize:10, fontWeight:700, letterSpacing:"1px", background:"rgba(43,54,217,0.15)", color:"var(--indigo_a700)" }}>
                  STATIC PROTOTYPE
                </div>
                <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", padding:"8px 16px", borderRadius:6, fontSize:12, color:"var(--gray_400)" }}>
                  As of Apr 1, 2026
                </div>
              </div>
            </div>

            {activeTab === "performance" && <PerformanceTab />}
            {activeTab === "allocation"  && <AllocationTab  />}
            {activeTab === "benchmark"   && <BenchmarkTab   />}

          </main>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity:0; transform:translateY(16px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </>
  );
}
